import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  InputAdornment,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { discountAPI } from '../../services/api';
import { Discount } from '../../types';

interface DiscountFormData {
  name: string;
  type: 'one time' | 'ongoing';
  category: 'percentage' | 'amount';
  value: number;
  description: string;
  isActive: boolean;
}

const initialFormData: DiscountFormData = {
  name: '',
  type: 'one time',
  category: 'percentage',
  value: 0,
  description: '',
  isActive: true
};

const DiscountPage: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [currentDiscountId, setCurrentDiscountId] = useState<string | null>(null);
  const [formData, setFormData] = useState<DiscountFormData>(initialFormData);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // Fetch all discounts
  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const response = await discountAPI.getAll();
      setDiscounts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching discounts:', err);
      setError('Failed to load discounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, []);

  // Handle form field changes for text inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle select field changes
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle value field changes specifically for numeric values
  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, or valid numbers
    if (value === '' || !isNaN(Number(value))) {
      setFormData({
        ...formData,
        value: value === '' ? 0 : Number(value)
      });
    }
  };

  // Open dialog for creating a new discount
  const handleOpenCreateDialog = () => {
    setFormData(initialFormData);
    setEditMode(false);
    setCurrentDiscountId(null);
    setDialogOpen(true);
  };

  // Open dialog for editing an existing discount
  const handleOpenEditDialog = (discount: Discount) => {
    setFormData({
      name: discount.name,
      type: discount.type,
      category: discount.category,
      value: discount.value,
      description: discount.description || '',
      isActive: discount.isActive
    });
    setEditMode(true);
    setCurrentDiscountId(discount.id);
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDeleteDialogOpen(false);
  };

  // Handle submit for creating or updating a discount
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.name.trim()) {
        setError('Discount name is required');
        return;
      }

      if (formData.value < 0) {
        setError('Value cannot be negative');
        return;
      }

      // Additional validation for percentage values
      if (formData.category === 'percentage' && formData.value > 100) {
        setError('Percentage value cannot exceed 100%');
        return;
      }

      if (editMode && currentDiscountId) {
        // Update existing discount
        await discountAPI.update(currentDiscountId, formData);
        setSuccess('Discount updated successfully!');
      } else {
        // Create new discount
        await discountAPI.create(formData);
        setSuccess('Discount created successfully!');
      }

      // Close dialog and refresh data
      setDialogOpen(false);
      fetchDiscounts();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Error saving discount:', err);
      setError('Failed to save discount. Please try again.');
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (id: string) => {
    setCurrentDiscountId(id);
    setDeleteDialogOpen(true);
  };

  // Handle delete discount
  const handleDelete = async () => {
    if (!currentDiscountId) return;

    try {
      await discountAPI.delete(currentDiscountId);
      setSuccess('Discount deleted successfully!');
      setDeleteDialogOpen(false);
      fetchDiscounts();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Error deleting discount:', err);
      setError('Failed to delete discount. Please try again.');
    }
  };

  // Helper function to format discount value for display
  const formatDiscountValue = (discount: Discount) => {
    if (discount.category === 'percentage') {
      return `${discount.value}%`;
    } else {
      return `$${discount.value.toFixed(2)}`;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumb navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >

        <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </RouterLink>
        <Typography color="text.primary">Discounts</Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Discount Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Create Discount
        </Button>
      </Box>

      {/* Alerts for error and success messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main content */}
      <Paper sx={{ p: 3, mb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : discounts.length === 0 ? (
          <Typography align="center" sx={{ p: 2 }}>
            No discounts found. Click the 'Create Discount' button to add one.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {discounts.map((discount) => (
                  <TableRow key={discount.id}>
                    <TableCell>{discount.name}</TableCell>
                    <TableCell>
                      {discount.type === 'one time' ? 'One Time' : 'Ongoing'}
                    </TableCell>
                    <TableCell>{formatDiscountValue(discount)}</TableCell>
                    <TableCell>{discount.description || '-'}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          backgroundColor: discount.isActive ? 'success.light' : 'error.light',
                          color: discount.isActive ? 'success.dark' : 'error.dark',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          display: 'inline-block'
                        }}
                      >
                        {discount.isActive ? 'Active' : 'Inactive'}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        startIcon={<EditIcon />}
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleOpenEditDialog(discount)}
                      >
                        Edit
                      </Button>
                      <Button
                        startIcon={<DeleteIcon />}
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(discount.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Discount' : 'Create Discount'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {editMode
              ? 'Update the discount details below.'
              : 'Enter the details for the new discount.'}
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="name"
                label="Discount Name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
                margin="dense"
              />
            </Grid>            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="discount-type-label">Type</InputLabel>
                <Select
                  labelId="discount-type-label"
                  id="discount-type"
                  name="type"
                  value={formData.type}
                  label="Type"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="one time">One Time</MenuItem>
                  <MenuItem value="ongoing">Ongoing</MenuItem>
                </Select>
              </FormControl>
            </Grid>            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="discount-category-label">Category</InputLabel>
                <Select
                  labelId="discount-category-label"
                  id="discount-category"
                  name="category"
                  value={formData.category}
                  label="Category"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="value"
                label="Value"
                value={formData.value}
                onChange={handleValueChange}
                fullWidth
                required
                margin="dense"
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.category === 'percentage' ? '%' : '$'}
                    </InputAdornment>
                  ),
                }}
                helperText={formData.category === 'percentage' ? 'Enter a value between 0 and 100' : ''}
              />
            </Grid>            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleChange}
                    name="isActive"
                  />
                }
                label="Active"
                sx={{ mt: 2 }}
              />
            </Grid>            <Grid size={{ xs: 12 }}>
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                margin="dense"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this discount? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DiscountPage;
